import { BaseService, PagedResult, QueryParams } from './base/base.service';
import { resolveRuntimeApiGatewayUrl } from '@/config/runtime-api';

/**
 * Wallet Balance DTO
 */
export interface WalletBalance {
  userId: string;
  currentBalance: number;
  lifetimeEarned?: number;
  lifetimeSpent?: number;
  lastUpdated?: string;
  lastReconciliation?: string;
  version?: number;
}

/**
 * Coin Transaction DTO
 */
export interface CoinTransaction {
  id: string;
  userId: string;
  changeType: 1 | 2; // 1=Credit, 2=Debit
  amount: number;
  description: string;
  category: string;
  referenceType?: string;
  referenceId?: string;
  status: number;
  balanceBefore?: number; // Optional - backend doesn't always return this
  balanceAfter: number;
  processedAt: string;
  creationTime: string;
  createdBy?: string;
}

/**
 * Wallet overview metrics
 */
export interface WalletOverviewMetrics {
  totalCoins: number;
  coinsToday: number;
  activeWallets: number;
  averagePerUser: number;
  coinDistribution: CoinDistributionPoint[];
  recentActivity: ActivityEntry[];
  changePercentages: {
    totalCoins: number;
    coinsToday: number;
    activeWallets: number;
    averagePerUser: number;
  };
}

/**
 * Coin distribution data point
 */
export interface CoinDistributionPoint {
  date: string;
  earned: number;
  spent: number;
  net: number;
}

/**
 * Activity feed entry
 */
export interface ActivityEntry {
  id: string;
  type: 'checkin' | 'reward_claim' | 'prize_draw' | 'adjustment' | 'transfer';
  description: string;
  userId: string;
  userName: string;
  amount: number;
  timestamp: string;
}

/**
 * User wallet summary
 */
export interface UserWalletSummary {
  userId: string;
  userName: string;
  userEmail: string;
  userAvatar?: string;
  balance: number;
  totalEarned: number;
  totalSpent: number;
  lastTransactionDate?: string;
  isActive: boolean;
}

/**
 * Admin coin adjustment request
 */
export interface AdminCoinAdjustmentRequest {
  userId: string;
  amount: number;
  description: string;
  referenceType?: string;
  referenceId?: string;
}

/**
 * Admin coin adjustment response
 */
export interface AdminCoinAdjustmentResponse {
  transactionId: string;
  userId: string;
  amount: number;
  newBalance: number;
  previousBalance: number;
  description: string;
  timestamp: string;
}

/**
 * Bulk adjustment request
 */
export interface BulkAdjustmentRequest {
  adjustments: AdminCoinAdjustmentRequest[];
}

/**
 * Bulk adjustment response
 */
export interface BulkAdjustmentResponse {
  success: boolean;
  totalProcessed: number;
  successfulAdjustments: number;
  failedAdjustments: number;
  results: AdminCoinAdjustmentResponse[];
  errors?: Array<{ userId: string; error: string }>;
}

/**
 * Wallet query parameters
 */
export interface WalletQueryParams extends QueryParams {
  search?: string;
  minBalance?: number;
  maxBalance?: number;
  status?: 'active' | 'inactive';
  sortBy?: 'balance' | 'earned' | 'userName' | 'lastActivity';
  sortOrder?: 'asc' | 'desc';
}

/**
 * Transaction query parameters
 */
export interface TransactionQueryParams extends QueryParams {
  userId?: string;
  changeType?: 1 | 2;
  category?: string;
  startDate?: string;
  endDate?: string;
}

/**
 * Wallet analytics data
 */
export interface WalletAnalytics {
  totalCoinsInCirculation: number;
  coinsDistributedToday: number;
  coinsDistributedThisWeek: number;
  coinsDistributedThisMonth: number;
  topWallets: Array<{
    userId: string;
    userName: string;
    balance: number;
  }>;
  transactionVolumeChart: Array<{
    date: string;
    volume: number;
    transactions: number;
  }>;
  distributionBySegment: Array<{
    segment: string;
    totalCoins: number;
    userCount: number;
  }>;
}

/**
 * Wallet Service
 * Provides wallet management functionality for admin panel
 */
export class WalletService extends BaseService {
  constructor() {
    super('/api/wallet');
  }

  /**
   * Get wallet overview metrics
   */
  async getOverviewMetrics(dateRange?: { from: string; to: string }): Promise<WalletOverviewMetrics> {
    const params = new URLSearchParams();
    if (dateRange) {
      params.append('startDate', dateRange.from);
      params.append('endDate', dateRange.to);
    }
    
    const response = await this.api.get<WalletOverviewMetrics>(`/analytics/overview?${params}`);
    return response.data;
  }

  /**
   * Get all user wallets with enriched user information
   */
  async getUserWallets(params: WalletQueryParams = {}): Promise<PagedResult<UserWalletSummary>> {
    const queryParams = new URLSearchParams();
    
    if (params.search) queryParams.append('search', params.search);
    if (params.minBalance !== undefined) queryParams.append('minBalance', params.minBalance.toString());
    if (params.maxBalance !== undefined) queryParams.append('maxBalance', params.maxBalance.toString());
    if (params.status) queryParams.append('status', params.status);
    if (params.sortBy) queryParams.append('sortBy', params.sortBy);
    if (params.sortOrder) queryParams.append('sortOrder', params.sortOrder);
    if (params.skipCount !== undefined) queryParams.append('skipCount', params.skipCount.toString());
    if (params.maxResultCount !== undefined) queryParams.append('maxResultCount', params.maxResultCount.toString());
    
    // Backend returns AdminWalletDto which may have userName/email or userId only
    interface BackendWalletDto {
      userId: string;
      userName?: string;
      email?: string;
      balance: number;
      lifetimeEarned: number;
      lifetimeSpent: number;
      lastTransactionDate?: string;
      isActive: boolean;
    }
    
    const response = await this.api.get<PagedResult<BackendWalletDto>>(`/coins/admin/wallets?${queryParams}`);
    
    // If backend doesn't provide user info, fetch it from Identity service
    const needsUserInfo = response.data.items.some(item => !item.userName || !item.email);
    
    if (needsUserInfo && response.data.items.length > 0) {
      // Fetch user information for all wallet users
      const userIds = response.data.items.map(item => item.userId);
      const userMap = await this.fetchUserInfo(userIds);
      
      // Map backend response with enriched user data
      const mappedItems: UserWalletSummary[] = response.data.items.map(item => {
        const userInfo = userMap.get(item.userId);
        
        // For orphaned wallets (no user in Identity service), show shortened userId for identification
        const displayName = item.userName 
          || userInfo?.userName 
          || userInfo?.name 
          || `Orphaned Wallet (${item.userId.substring(0, 8)}...)`;
        
        const email = item.email || userInfo?.email || 'N/A';
        console.log(`Mapping wallet for user ${item.userId}: name="${displayName}", email="${email}", backend_email="${item.email}", fetched_email="${userInfo?.email}"`);
        
        return {
          userId: item.userId,
          userName: displayName,
          userEmail: email,
          balance: item.balance ?? 0,
          totalEarned: item.lifetimeEarned ?? 0,
          totalSpent: item.lifetimeSpent ?? 0,
          lastTransactionDate: item.lastTransactionDate,
          isActive: item.isActive ?? false,
        };
      });
      
      return {
        items: mappedItems,
        totalCount: response.data.totalCount,
      };
    }
    
    // Backend provided user info, use it directly
    const mappedItems: UserWalletSummary[] = response.data.items.map(item => ({
      userId: item.userId,
      userName: item.userName || 'Unknown User',
      userEmail: item.email || 'N/A',
      balance: item.balance ?? 0,
      totalEarned: item.lifetimeEarned ?? 0,
      totalSpent: item.lifetimeSpent ?? 0,
      lastTransactionDate: item.lastTransactionDate,
      isActive: item.isActive ?? false,
    }));
    
    return {
      items: mappedItems,
      totalCount: response.data.totalCount,
    };
  }

  /**
   * Fetch user information from Identity service
   * @private
   */
  private async fetchUserInfo(userIds: string[]): Promise<Map<string, { userName: string; email: string; name?: string }>> {
    const userMap = new Map<string, { userName: string; email: string; name?: string }>();
    
    try {
      // Import axios for direct API calls
      const axios = (await import('axios')).default;
      
      // Get auth token
      const { tokenProvider } = await import('./base/token-provider');
      const token = await tokenProvider.getAccessToken();
      
      const failedUsers: string[] = [];
      
      // Fetch users in batch using direct API calls to Identity service
      const promises = userIds.map(async (userId) => {
        try {
          // Call Identity API directly through the gateway
          const response = await axios.get<{
            id: string;
            userName: string;
            email: string;
            name?: string;
            surname?: string;
          }>(`${resolveRuntimeApiGatewayUrl()}/api/identity/users/${userId}`, {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
          });
          
          console.log(`✓ User ${userId} data:`, response.data);
          
          if (response.data) {
            const fullName = [response.data.name, response.data.surname]
              .filter(Boolean)
              .join(' ') || response.data.userName;
            
            userMap.set(userId, {
              userName: fullName,
              email: response.data.email,
              name: fullName,
            });
            
            console.log(`  → Display name: "${fullName}"`);
          }
        } catch (error) {
          // If user not found or error, track it
          failedUsers.push(userId);
          const err = error as { response?: { status?: number; data?: unknown }; message?: string };
          console.log(`✗ User ${userId} failed: HTTP ${err?.response?.status}`, err?.response?.data);
        }
      });
      
      await Promise.all(promises);
      
      // Summary log
      if (failedUsers.length > 0) {
        console.warn(`⚠️ Wallet Service: ${failedUsers.length} user(s) not found in Identity service (orphaned wallets):`, failedUsers);
      }
      if (userMap.size > 0) {
        console.log(`✓ Wallet Service: Successfully loaded ${userMap.size} user profiles`);
      }
    } catch (error) {
      console.error('Failed to fetch user information:', error);
    }
    
    return userMap;
  }

  /**
   * Get wallet details for a specific user
   */
  async getUserWalletDetails(userId: string): Promise<WalletBalance> {
    const response = await this.api.get<WalletBalance>(`/coins/admin/balance/${userId}`);
    return response.data;
  }

  /**
   * Get transaction history for a specific user
   */
  async getUserTransactions(userId: string, params: TransactionQueryParams = {}): Promise<PagedResult<CoinTransaction>> {
    const queryParams = new URLSearchParams();
    
    if (params.changeType !== undefined) queryParams.append('changeType', params.changeType.toString());
    if (params.category) queryParams.append('category', params.category);
    if (params.startDate) queryParams.append('startDate', params.startDate);
    if (params.endDate) queryParams.append('endDate', params.endDate);
    if (params.skipCount !== undefined) queryParams.append('skipCount', params.skipCount.toString());
    if (params.maxResultCount !== undefined) queryParams.append('maxResultCount', params.maxResultCount.toString());
    
    const response = await this.api.get<PagedResult<CoinTransaction>>(`/coins/admin/transactions/${userId}?${queryParams}`);
    return response.data;
  }

  /**
   * Get audit log of all admin wallet adjustments
   * Returns all transactions that were created by an admin
   */
  async getAuditLog(params: TransactionQueryParams = {}): Promise<PagedResult<CoinTransaction>> {
    const queryParams = new URLSearchParams();
    
    if (params.changeType !== undefined) queryParams.append('changeType', params.changeType.toString());
    if (params.startDate) queryParams.append('startDate', params.startDate);
    if (params.endDate) queryParams.append('endDate', params.endDate);
    if (params.skipCount !== undefined) queryParams.append('skipCount', params.skipCount.toString());
    if (params.maxResultCount !== undefined) queryParams.append('maxResultCount', params.maxResultCount.toString());
    
    const response = await this.api.get<PagedResult<CoinTransaction>>(`/admin/audit-log?${queryParams}`);
    return response.data;
  }

  /**
   * Adjust wallet balance (add/subtract coins)
   */
  async adjustWalletBalance(request: AdminCoinAdjustmentRequest): Promise<AdminCoinAdjustmentResponse> {
    const response = await this.api.post<AdminCoinAdjustmentResponse>('/coins/admin/adjust', request);
    return response.data;
  }

  /**
   * Bulk adjust wallet balances
   */
  async bulkAdjustWallets(request: BulkAdjustmentRequest): Promise<BulkAdjustmentResponse> {
    const response = await this.api.post<BulkAdjustmentResponse>('/coins/admin/bulk-adjust', request);
    return response.data;
  }

  /**
   * Get wallet analytics
   */
  async getWalletAnalytics(dateRange?: { from: string; to: string }): Promise<WalletAnalytics> {
    const params = new URLSearchParams();
    if (dateRange) {
      params.append('startDate', dateRange.from);
      params.append('endDate', dateRange.to);
    }
    
    const response = await this.api.get<WalletAnalytics>(`/analytics/wallet-stats?${params}`);
    return response.data;
  }

  /**
   * Export wallets to CSV
   */
  async exportWallets(params: WalletQueryParams = {}): Promise<Blob> {
    const queryParams = new URLSearchParams();
    if (params.search) queryParams.append('search', params.search);
    if (params.minBalance !== undefined) queryParams.append('minBalance', params.minBalance.toString());
    if (params.maxBalance !== undefined) queryParams.append('maxBalance', params.maxBalance.toString());
    
    const response = await this.api.get(`/coins/admin/wallets/export?${queryParams}`, {
      responseType: 'blob',
    });
    return response.data;
  }

  /**
   * Export transactions to CSV
   */
  async exportTransactions(userId: string, params: TransactionQueryParams = {}): Promise<Blob> {
    const queryParams = new URLSearchParams();
    if (params.startDate) queryParams.append('startDate', params.startDate);
    if (params.endDate) queryParams.append('endDate', params.endDate);
    
    const response = await this.api.get(`/coins/admin/transactions/${userId}/export?${queryParams}`, {
      responseType: 'blob',
    });
    return response.data;
  }
}

// Export singleton instance
export const walletService = new WalletService();
